import type { ReactNode } from "react";

import clsx from "clsx";

import {
  BaseAlertCircleIcon,
  BaseAlertTriangleIcon,
  BaseInfoIcon,
  CheckCircleIcon,
} from "../Icons";
import { Typo, type TypoStyle } from "../Typo";

export type InlineNotificationVariant =
  | "info"
  | "warning"
  | "error"
  | "neutral"
  | "tonal"
  | "success";

type InlineNotificationProps = {
  variant: InlineNotificationVariant;
  children: ReactNode;
  className?: string;
  /**
   * Overrides the variant's default state icon. For notices where the glyph
   * carries the meaning rather than the severity — e.g. the archived-program
   * notice uses `BaseArchiveIcon` on the `neutral` colour scheme. Colour still
   * comes from the variant, so the icon must be a `currentColor` SVG.
   */
  icon?: typeof BaseInfoIcon;
  /** Optional trailing slot for an inline action (e.g. a Refresh link), rendered right-aligned. */
  action?: ReactNode;
  "data-testid"?: string;
};

const VARIANT_STYLES: Record<
  InlineNotificationVariant,
  {
    bg: string;
    iconColor: string;
    textColor: string;
    typoType: TypoStyle;
    Icon: typeof BaseInfoIcon;
  }
> = {
  info: {
    bg: "bg-surface-brand-subtle",
    iconColor: "text-text-brand-bold",
    textColor: "text-text-brand-bold",
    typoType: "bodySM",
    Icon: BaseInfoIcon,
  },
  warning: {
    bg: "bg-surface-accent-amber-subtler",
    iconColor: "text-text-warning-boldest",
    textColor: "text-text-warning-boldest",
    typoType: "bodySM",
    Icon: BaseAlertCircleIcon,
  },
  error: {
    bg: "bg-surface-accent-red-subtler",
    iconColor: "text-text-negative-bold",
    textColor: "text-text-negative-bold",
    typoType: "bodySM",
    Icon: BaseAlertTriangleIcon,
  },
  // Unstyled/low-emphasis notice — a statement of fact rather than a severity
  // (the archived-program explainer). Icon and text take their own semantic
  // token family rather than one shared colour, so each lands on the DS value
  // for its element type.
  neutral: {
    bg: "bg-surface-secondary",
    iconColor: "text-icon-secondary",
    textColor: "text-text-secondary",
    typoType: "bodySM",
    Icon: BaseInfoIcon,
  },
  // Phase 2 closed-program banner: tonal fill with primary 12px text.
  tonal: {
    bg: "bg-surface-tonal-default",
    iconColor: "text-icon-secondary",
    textColor: "text-text-primary",
    typoType: "bodyXS",
    Icon: BaseInfoIcon,
  },
  // `CheckCircleIcon` hardcodes its stroke, so the path is pointed at
  // `currentColor` to take the variant's text token.
  success: {
    bg: "bg-surface-accent-green-subtler",
    iconColor: "text-text-positive-bold [&_path]:stroke-current",
    textColor: "text-text-positive-bold",
    typoType: "labelSM",
    Icon: CheckCircleIcon,
  },
};

/** Inline notice — info/warning/error/neutral/success banner with a leading state icon. No coloured left-border accent. */
export const InlineNotification = ({
  variant,
  children,
  className,
  icon,
  action,
  "data-testid": testId,
}: InlineNotificationProps) => {
  const {
    bg,
    iconColor,
    textColor,
    typoType,
    Icon: VariantIcon,
  } = VARIANT_STYLES[variant];
  const Icon = icon ?? VariantIcon;

  return (
    <div
      data-testid={testId}
      role={variant === "error" ? "alert" : "status"}
      className={clsx("flex items-start gap-2 rounded-lg p-4", bg, className)}
    >
      <span className={clsx("mt-0.5 shrink-0", iconColor)}>
        <Icon height={16} width={16} aria-hidden="true" />
      </span>
      <Typo
        element="p"
        type={typoType}
        textColor={textColor}
        className="flex-1"
      >
        {children}
      </Typo>
      {action && <span className="shrink-0">{action}</span>}
    </div>
  );
};
